import { PITCH_CONFIDENCE_MIN } from '@muzly/shared';
import { clampNum } from '@muzly/voice-engine';

import { yOfWindow } from './pitchWindow';
import { buildSegments } from '../../../components';

import type { MidiWindow } from './pitchWindow';
import type { PitchGraphPoint } from '../../../components';
import type { SungRange } from '../capture';
import type { MelodyNote } from '../melody';

/**
 * traceGeometry — 측정 트랙의 순수 기하. 시간축 음정 곡선을 SVG 경로로 옮긴다.
 *
 * **좌표는 0..100 두 축의 view 단위다.** 컨테이너를 재지 않으므로 DOM 없이 계산되고,
 * 화면에서는 `viewBox="0 0 100 100"` + `preserveAspectRatio="none"`로 늘려 쓴다
 * (선 굵기는 `vector-effect="non-scaling-stroke"`가 지킨다). 컨테이너 크기를 측정해서
 * 좌표를 만들면 이 모듈이 레이아웃에 묶여 jsdom에서 전부 0이 된다 — 이미 한 번 그 방식으로
 * 트랙이 통째로 안 보였다.
 *
 * 세로 축은 `pitchWindow`가 잡는다: 창은 옥타브 단위로만 자라고 중심이 격자에 붙는다.
 * 건반도 눈금도 없는 화면에서 축까지 미끄러지면 내가 움직인 건지 화면이 움직인 건지
 * 알 수 없기 때문이다.
 *
 * 발성 머리(`freshHead`)는 **두 그림이 공유한다** — 지속음 막대와 이 곡선이 "소리가
 * 끊겼다"를 서로 다른 시점에 판정하면 같은 화면에서 다른 말을 하게 된다.
 *
 * React·DOM 의존 없음 — vitest 대상.
 */

/** view 한 변(가로·세로 공통). 0..100이라 좌표가 곧 퍼센트다. */
const VIEW = 100;

/** 이보다 오래 유성 프레임이 없으면 "지금 내는 중"이 아니다 — 표식을 지운다. */
export const HEAD_FRESH_MS = 200;

/**
 * 가로축 — 테이크의 어느 시각이 띠의 왼쪽 끝이고 띠 폭 전체가 얼마를 담을지.
 *
 * **좌표는 정적이다 — 곡 전체를 한 띠에 한 번 깐다.** 흐르는 화면은 좌표를 다시 계산해서
 * 만들지 않고 그 띠를 `translateX`로 민다(`stripLayout`·`stripShiftPct`). c28f336 은
 * 프레임마다 원점을 옮겨 모든 점·블록의 x 를 다시 썼고(약 47Hz), 실기기 판정으로 정적 축에
 * 되돌아갔다(01c954a — "다음 음을 미리 읽기 좋다"). 다시 흐르게 한 결정은 커서를 30% 에 둬
 * 앞으로 7초를 보이는 쪽으로 그 사유에 답한다.
 */
export interface TraceAxis {
  /** x=0에 놓일 시각(ms). 테이크 시작이지 0이 아니다. */
  startMs: number;
  /** 띠 폭 전체가 담는 시간(ms) — 테이크 전체. */
  spanMs: number;
}

/** 유성이 확정된 점 — `buildSegments`를 통과한 점은 전부 이 모양이다. */
type VoicedPoint = PitchGraphPoint & { midi: number };

/**
 * 그릴 수 있는 점인가. **`buildSegments`와 같은 선**을 쓴다(`PITCH_CONFIDENCE_MIN`) —
 * 곡선은 저신뢰에서 끊기는데 표식만 그 프레임을 그리면, 선이 없는 자리에 동그라미가
 * 혼자 서 있다. 그 프레임이 정확히 검출기가 옥타브를 헛짚는 구간이라, 표식만 옥타브
 * 위로 튀어 보이는 일이 실제로 있었다(`.claude/20260916/DEBUG-14-32-00.md`).
 */
const isVoiced = (p: PitchGraphPoint): p is VoicedPoint =>
  p.midi != null && p.confidence >= PITCH_CONFIDENCE_MIN;

/** 소수점 둘째 자리까지 — 경로 문자열이 길어지면 diff도 테스트도 못 읽는다. */
const fmt = (n: number): number => Math.round(n * 100) / 100;

export const xOfTime =
  ({ startMs, spanMs }: TraceAxis) =>
  (t: number): number =>
    fmt(VIEW * clampNum((t - startMs) / spanMs, 0, 1));

/**
 * 세로 플레이헤드가 서는 자리 — 그리는 영역 가로의 비율. 뒤로 3초, 앞으로 7초를 보인다
 * (팀 vault `wiki/pitch-following-visualization` 의 30%/70% 패턴).
 */
export const TRACE_CURSOR_AT = 0.3;

/** 흐르는 축에서 그리는 영역 한 폭이 담는 시간(초). */
export const TRACE_VISIBLE_SEC = 10;

/**
 * 띠 폭 — 그리는 영역 폭 대비 %. 곡이 보이는 창보다 길면 100 을 넘는다.
 */
export const stripLayout = (
  { spanMs }: TraceAxis,
  visibleMs: number,
): number => (visibleMs > 0 ? (VIEW * spanMs) / visibleMs : VIEW);

/**
 * 띠를 얼마나 밀어야 `nowMs` 가 커서 자리에 오는가 — **띠 자기 폭 기준 %** 다
 * (`translateX(%)` 가 자기 폭을 기준으로 잡으므로). 영역 폭을 1, 띠 폭을 W = span/visible,
 * 띠 안의 now 를 f 라 하면 밀린 뒤의 now 는 영역의 (f + shift) × W 이고, 그것이 커서가 되는
 * shift 는 cursorAt/W − f 다.
 *
 * 축 밖의 now 는 양끝에 붙인다 — 테이크가 끝난 뒤에 온 프레임이 띠를 곡 밖으로 밀면
 * 멜로디가 화면에서 사라진다.
 */
export const stripShiftPct = (
  nowMs: number,
  axis: TraceAxis,
  visibleMs: number,
  cursorAt = TRACE_CURSOR_AT,
): number => {
  if (axis.spanMs <= 0) {
    return 0;
  }
  const f = clampNum((nowMs - axis.startMs) / axis.spanMs, 0, 1);
  return fmt(VIEW * ((cursorAt * visibleMs) / axis.spanMs - f));
};

/**
 * 지금 내고 있는 음. 마지막 유성 프레임이 오래됐으면 null — 표식이 남아 있으면
 * "아직 잡히는 중"이라는 거짓말이 된다.
 */
export const freshHead = (
  points: PitchGraphPoint[],
  freshMs = HEAD_FRESH_MS,
): VoicedPoint | null => {
  const latest = points.length ? points[points.length - 1].t : 0;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const point = points[i];
    if (isVoiced(point)) {
      return latest - point.t <= freshMs ? point : null;
    }
  }
  return null;
};

/**
 * 이웃한 두 점이 이보다 크게 벌어지면 선을 끊는다. 오검출이 서는 자리는 옥타브(12)·12도(19)·
 * 버스트(24+)고, 부른 도약은 대개 그 아래다 — 처음엔 엔진의 `is_big_jump`(7반음)에 맞췄다가
 * 5도·6도를 오르내린 테이크가 도약마다 끊겼다(실기기 2026-09-21 15:51).
 */
const LEAP_SEMITONES = 11;

/**
 * 구간 사이의 구멍이 이보다 짧으면 한 줄로 잇는다. 도약하는 2~5프레임은 분석 창 하나에 두 음이
 * 걸려 주기성이 실제로 무너지고(NSDF 0.25~0.44), 엔진이 다시 붙는 프레임까지 합쳐
 * 0.13~0.15초가 빈다 — 부른 사람은 끊은 적이 없다. 표식이 "아직 내는 중"이라고 보는 선과
 * 같은 값이어야 한다: 표식은 살아 있는데 선은 끊겨 있으면 같은 화면이 두 말을 한다.
 */
const BRIDGE_MS = HEAD_FRESH_MS;

/** 짧은 구멍을 사이에 둔 구간들을 하나로 잇는다. */
const bridgeShortGaps = (segments: VoicedPoint[][]): VoicedPoint[][] =>
  segments.reduce<VoicedPoint[][]>((runs, segment) => {
    const run = runs[runs.length - 1];
    if (run != null && segment[0].t - run[run.length - 1].t <= BRIDGE_MS) {
      run.push(...segment);
    } else {
      runs.push([...segment]);
    }
    return runs;
  }, []);

/**
 * 옥타브급 도약에서 구간을 가른다. 엔진은 옥타브 오검출을 막느라 진짜 도약도 0.2~0.3초
 * 붙들었다가 한 프레임에 옮기는데(`post_processor.hpp` kAdoptConfSum·kHoldConfSum), 그 두 점을
 * 이으면 부르지 않은 수직선이 남는다. 점은 버리지 않는다 — 선만 끊는다. 시간이 벌어진 두 점
 * 사이의 차이는 글라이드라 건드리지 않는다.
 */
const splitAtLeaps = (segment: VoicedPoint[]): VoicedPoint[][] =>
  segment.reduce<VoicedPoint[][]>((runs, point) => {
    const run = runs[runs.length - 1];
    const prev = run?.[run.length - 1];
    const leaps =
      prev != null &&
      point.t - prev.t <= BRIDGE_MS &&
      Math.abs(point.midi - prev.midi) > LEAP_SEMITONES;
    if (run == null || leaps) {
      runs.push([point]);
    } else {
      run.push(point);
    }
    return runs;
  }, []);

/**
 * 끊기지 않는 발성 구간마다 경로 하나. 무성·저신뢰 프레임을 가려내는 판정은 `buildSegments`
 * 몫이다 — 그래프가 따로 기준을 두면 "엔진은 유성이라는데 그래프는 끊긴다"가 생긴다.
 *
 * 점이 하나뿐인 구간도 길이 0인 선분으로 낸다. `M`만 내면 아무것도 안 그려져서, 한 프레임만
 * 잡힌 소리가 "못 들었다"로 보인다.
 */
export const tracePaths = (
  points: PitchGraphPoint[],
  axis: TraceAxis,
  window: MidiWindow,
): string[] => {
  const x = xOfTime(axis);
  const y = yOfWindow(window, VIEW);
  const at = (p: VoicedPoint): string => `${x(p.t)} ${fmt(y(p.midi))}`;
  const voicedRuns = buildSegments(points)
    .map((segment) => segment.filter(isVoiced))
    .filter((segment) => segment.length > 0);
  return bridgeShortGaps(voicedRuns)
    .flatMap(splitAtLeaps)
    .map((segment) => {
      const rest = segment.length > 1 ? segment.slice(1) : segment;
      return `M ${at(segment[0])} ${rest.map((p) => `L ${at(p)}`).join(' ')}`;
    });
};

/** 가이드 멜로디 한 음이 놓일 자리(%). 가로는 시간, 세로 중심은 음높이. */
export interface GuideBlock {
  id: string;
  x: number;
  width: number;
  y: number;
}

/**
 * 부를 멜로디를 화면에 깔 자리들.
 *
 * **시간 원점은 축의 원점이다.** 멜로디의 `startMs`는 음원 t=0 기준이고 음원은 녹음이
 * 시작되는 순간 튼다(`SongReferenceAudio`) — 채점(`scoreSong`)도 같은 앵커, 즉 테이크의 첫
 * 프레임 타임스탬프를 쓴다. 가이드가 자기 시계를 쓰면 화면에서는 겹쳤는데 점수는 틀린
 * 상태가 나온다.
 *
 * 축 밖으로 나간 음은 버린다 — 왼쪽 끝에 눌러 붙인 폭 0짜리 블록은 없는 음을 있다고 말한다.
 */
export const guideBlocks = (
  melody: MelodyNote[],
  axis: TraceAxis,
  window: MidiWindow,
): GuideBlock[] => {
  const y = yOfWindow(window, VIEW);
  return melody
    .filter((note) => note.startMs >= 0 && note.startMs < axis.spanMs)
    .map((note) => ({
      id: note.id,
      x: fmt((VIEW * note.startMs) / axis.spanMs),
      width: fmt(
        (VIEW * Math.min(note.durMs, axis.spanMs - note.startMs)) / axis.spanMs,
      ),
      y: fmt(y(note.midi)),
    }));
};

/**
 * 가이드가 덮는 음높이 폭 — 세로 창이 **반드시 포함해야** 하는 범위다. 부른 폭만 보고 창을
 * 잡으면 아직 안 부른 가이드의 높은 음이 화면 위로 잘려 나간다.
 */
export const guideRange = (melody: MelodyNote[]): SungRange | null =>
  melody.length === 0
    ? null
    : {
        lo: Math.min(...melody.map((n) => n.midi)),
        hi: Math.max(...melody.map((n) => n.midi)),
      };

/** 곡선의 리딩 엣지 — 도트가 붙을 자리. 곡선의 마지막 좌표와 같은 값이다. */
export const headPoint = (
  points: PitchGraphPoint[],
  axis: TraceAxis,
  window: MidiWindow,
): { x: number; y: number } | null => {
  const head = freshHead(points);
  return head == null
    ? null
    : { x: xOfTime(axis)(head.t), y: fmt(yOfWindow(window, VIEW)(head.midi)) };
};

/**
 * 왼쪽 음이름 축의 행들 — 창 안의 각 반음에 `{ midi, y, label }`을 준다.
 *
 * **예전에는 이 축이 없었다.** "절대 음높이는 보여주지 않는다"는 결정이었고, 근거는
 * 음이름이 판단에 안 쓰인다는 것이었다. 노래 진단에서는 그게 성립하지 않는다 — 정답
 * 블록과 내 곡선이 **어느 음에서** 어긋났는지가 곧 피드백이고, 시안(`1017:17872`)도
 * 왼쪽에 `C3`~`B3`를 세운다.
 *
 * 창이 넓어지면 라벨을 한 칸씩 건너뛴다. 2옥타브 창에 25줄을 다 적으면 글자가 서로
 * 겹쳐서, 축이 있는데 못 읽는 상태가 된다.
 */
export interface AxisRow {
  midi: number;
  /** 0..100 view 단위 세로 좌표(0 = 맨 위). */
  y: number;
}

/**
 * 라벨 사이 최소 간격(view 단위, 100 = 트랙 전체 높이). 3.6 은 "글자가 겹치지 않는
 * 하한"이었고, 실기기에서 3옥타브 창이 잡히면 19줄이 빽빽한 표처럼 읽혔다(2026-09-21).
 * 5.6 이면 창 폭과 무관하게 라벨 간격이 8% 안팎으로 수렴한다 — 12반음이면 반음마다,
 * 24반음이면 온음마다, 36반음이면 세 반음마다 한 줄이다. 시안(약 19반음)보다 성글지만,
 * 축은 곡선이 어디쯤인지 읽는 기준이지 음이름을 세는 표가 아니다.
 */
export const AXIS_MIN_GAP = 5.6;

export const axisRows = (window: MidiWindow): AxisRow[] => {
  const span = window.hi - window.lo;
  if (span <= 0) {
    return [];
  }
  // 반음 한 칸의 세로 간격이 최소치보다 좁으면 2칸·3칸으로 성기게 간다.
  const step = Math.max(1, Math.ceil(AXIS_MIN_GAP / (100 / span)));
  const y = yOfWindow(window, 100);
  const rows: AxisRow[] = [];
  for (let midi = Math.ceil(window.lo); midi <= window.hi; midi += step) {
    rows.push({
      midi,
      y: y(midi),
    });
  }
  return rows;
};
